import styles from "../../css/messages.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMessages } from "../../../../hooks/useMessages";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import API_URLS from "../../../../constants/apiConstants";
import { LoadingBar } from "../../../../components/general/LoadingBar";

export const ChatArea = ({
  activeInbox,
  activeGroup,
  setActiveGroup,
  updateInbox,
  getMemberName,
  setSelectedImage,
  setShowImageModal,
}) => {
  const { t, i18n } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const chatBodyRef = useRef(null);
  const savedScrollBottomRef = useRef(null);
  const shouldRestoreScrollRef = useRef(false);
  const unreadDividerRef = useRef(null);
  const lastMessageRef = useRef(null);
  const [displayUnreadCount, setDisplayUnreadCount] = useState(0);
  const {
    messages,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    refreshMessages,
    isConnected,
    justSentMessage,
    setJustSentMessage,
    handleSendMessage,
  } = useMessages(activeGroup?.group_name);

  const scrollToBottom = useCallback(() => {
    const container = chatBodyRef.current;
    const divider = unreadDividerRef.current;
    requestAnimationFrame(() => {
      // Case 1: Unread divider exists → center it
      if (displayUnreadCount > 0 && divider) {
        const containerRect = container.getBoundingClientRect();
        const dividerRect = divider.getBoundingClientRect();

        const offset =
          dividerRect.top -
          containerRect.top -
          containerRect.height / 2 +
          dividerRect.height / 2;

        container.scrollTop += offset;
      }
      // Case 2: No unread messages → scroll to last message
      else if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({
          behavior: "auto",
          block: "end",
        });
      }
    });
  }, [displayUnreadCount]);

  // Fetch group messages
  useEffect(() => {
    if(isConnected) {
      refreshMessages();
    }
  }, [isConnected, activeGroup?.group_name, refreshMessages]);

  // Mark group as read if there are unread messages
  useEffect(() => {
    const updateInboxGroupsCount = () => {
      setActiveGroup((prevGroup) =>
        prevGroup ? { ...prevGroup, unread_count: 0 } : prevGroup
      );

      updateInbox(activeInbox, (prevInboxes) => {
        if (!prevInboxes) return prevInboxes;

        return prevInboxes.map((g) =>
          {
            if (g.group_name === activeGroup?.group_name) {
              return {
                ...g,
                unread_count: 0,
              };
            }
            return g;
          }
        );
      });
    };

    const markGroupAsRead = async () => {
      try {
        const api_url = API_URLS.markGroupAsRead(activeGroup?.group_name);
        const response = await axiosPrivate.post(api_url);

        // If response is OK, set the unread_count to 0
        if (response?.status === 200) {
          updateInboxGroupsCount();
        }
      } catch (error) {
        console.error("Failed to mark group as read:", error);
      }
    };

    // Run this effect whenever activeGroup changes AND has unread messages
    if (activeGroup?.group_name && activeGroup?.unread_count > 0) {
      markGroupAsRead();
    }
  }, [
    activeGroup?.group_name,
    activeGroup?.unread_count,
    activeInbox,
    axiosPrivate,
    setActiveGroup,
    updateInbox,
  ]);

  useEffect(() => {
    setDisplayUnreadCount(activeGroup?.unread_count);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activeGroup?.group_name]);

  // If a message is sent, reset the new-messages display count (to hide the divider)
  useEffect(() => {
    if (justSentMessage) {
      setDisplayUnreadCount(0);
      setJustSentMessage(false);
    }
  },[justSentMessage, setJustSentMessage]);

  useEffect(() => {
    if (!chatBodyRef.current || !messages || messages.length === 0) return;

    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Loading more messages when scrolling up and preserving scroll position
  const handleScroll = useCallback(
    (e) => {
      const element = e.currentTarget;
      if (!element) return;

      const THRESHOLD = 100;
      const isNearTop = element.scrollTop <= THRESHOLD;

      if (isNearTop && hasMore && !isLoading) {
        // Save distance-from-bottom BEFORE loading
        savedScrollBottomRef.current = element.scrollHeight - element.scrollTop;
        shouldRestoreScrollRef.current = true;

        loadMore();
      }
    },
    [hasMore, isLoading, loadMore]
  );

  useEffect(() => {
    if (!shouldRestoreScrollRef.current) return;

    const el = chatBodyRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      const distFromBottom = savedScrollBottomRef.current || 0;
      el.scrollTop = el.scrollHeight - distFromBottom;

      // cleanup
      shouldRestoreScrollRef.current = false;
      savedScrollBottomRef.current = null;
    });
  }, [messages]);

  const chatBodyBorderStyle = () => {
    if (i18n.dir() === "ltr") {
      return {
        borderTopRightRadius: activeGroup ? "0" : "30px",
        borderBottomRightRadius: activeGroup ? "0" : "30px",
      };
    }
    return {
      borderTopLeftRadius: activeGroup ? "0" : "30px",
      borderBottomLeftRadius: activeGroup ? "0" : "30px",
    };
  };

  return (
    <div className={styles.chatContainer}>
      {activeGroup && (
        <>
          {/* Chat header */}
          <ChatHeader activeGroup={activeGroup} getMemberName={getMemberName} />

          {/* Loading Bar */}
          {!isConnected && <LoadingBar />}
        </>
      )}

      <div
        ref={chatBodyRef}
        id="scrollableChatDiv"
        className={styles.chatBody}
        style={chatBodyBorderStyle()}
        onScroll={handleScroll}
      >
        {isLoading && !isLoadingMore ? (
          // 1. Loading spinner
          <div className="h-100 d-flex justify-content-center align-items-center">
            <Spinner size="sm" style={{ color: "#777" }} />
          </div>
        ) : messages === null ? (
          // 2. No chat selected
          <div className="h-100 d-flex justify-content-center align-items-center text-muted">
            {t("admin_pages.messages.select_chat")}
          </div>
        ) : messages.length === 0 ? (
          // 3. Chat selected but empty
          <div className="h-100 d-flex justify-content-center align-items-center text-muted">
            {t("admin_pages.dashboard.no_messages")}
          </div>
        ) : (
          // 4. Show messages list
          <MessageList
            messages={messages}
            getMemberName={getMemberName}
            setSelectedImage={setSelectedImage}
            setShowImageModal={setShowImageModal}
            displayUnreadCount={displayUnreadCount}
            unreadDividerRef={unreadDividerRef}
            lastMessageRef={lastMessageRef}
          />
        )}
      </div>

      {/* Message Input */}
      {activeGroup && (
        <MessageInput
          groupName={activeGroup?.group_name}
          handleSendMessage={handleSendMessage}
        />
      )}
    </div>
  );
};
